import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { PaymentProcessEnum, PaymentStatusEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { Payment } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { usePayments } from '../../../lib/util/hooks/payouts/PaymentHook';
import { TextHelpers } from '../../../lib/util/TextHelpers';

import ArrowBack from '../../../assets/img/arrow-left.svg';
import noMessagesLogo from '../../../assets/img/icon-no-date-range.svg';
import Modal from '../../../containers/Funds/modal/paymentsModal/BasicModal';
import TransactionItem from '../../../containers/Funds/transactions/TransactionItem';
import { useIsMobile } from '../../../hooks';
import MobileFromToDatePicker from '../../../shared_components/MobileCalendar/MobileFromToDatePicker';
import { ClickableDiv, ClickableImage } from '../..';

import styles from './PayoutHistoryPage.module.scss';

const ALLOWED_STATUSES = new Set<number>([
  PaymentStatusEnum.ForApproval,
  PaymentStatusEnum.OnHold,
  PaymentStatusEnum.Completed,
]);

const isPayout = (p: Payment) =>
  p.is_cash_account === true
  && p.payment_process === PaymentProcessEnum.Withdrawal
  && ALLOWED_STATUSES.has(p.payment_status);

const PayoutHistoryPage = () => {
  const { isMobile, isTablet } = useIsMobile();
  const navigate = useNavigate();

  const [infoModal, setInfoModal] = useState({ values: {} });
  const [showModal, setShowModal] = useState<boolean>(false);

  const {
    hasMorePages,
    number: pageNumber,
    setDateRange,
    setPage,
    filteredContent,
  } = usePayments();

  const allTransactions = useSelector<RootState, Payment[]>(
    (state) => state.payment.paymentsAll.data,
  );

  const payoutsFromHook = useMemo(
    () =>
      (filteredContent || [])
        .filter((item) => item.isPayment && isPayout(item.orderOrPayment as Payment))
        .sort(
          (a, b) =>
            new Date((b.orderOrPayment as Payment).create_date).getTime()
            - new Date((a.orderOrPayment as Payment).create_date).getTime(),
        ),
    [filteredContent],
  );

  const oldestPayoutDate = useMemo(() => {
    const payoutsAll = (allTransactions || [])
      .filter(isPayout)
      .sort((a, b) => new Date(a.create_date).getTime() - new Date(b.create_date).getTime());
    return payoutsAll.length ? new Date(payoutsAll[0].create_date) : new Date();
  }, [allTransactions]);

  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isAtBottom = scrollTop + windowHeight >= documentHeight - 1;
    if (isAtBottom && hasMorePages) {
      setPage(pageNumber + 1);
    }
  };

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [isMobile, hasMorePages, pageNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (value) => {
    const { startDate, endDate } = value;
    setDateRange({ startDate, endDate });
  };

  const handleModalTransfer = () => setShowModal((s) => !s);

  const handleClick = (payment: Payment) => {
    const values = {
      hasLinkedAccounts: true,
      amount: payment.amount,
      create_date: payment.create_date,
      payment_method: payment.payment_method,
      category: TextHelpers.getPaymentProcessName(payment.payment_process),
      adjustmentCategory: payment.category,
      bankName: payment.sender_bank_name,
      paymentReference: payment.payment_reference,
      paymentStatus: payment.payment_status,
      paymentProcess: payment.payment_process,
      description: payment.description,
      payment,
    };
    setInfoModal({ values });
    handleModalTransfer();
  };

  return (
    <div className={styles.wrapper}>
      {isMobile && (
        <div className={styles.arrowBack}>
          <ClickableImage src={ArrowBack} onClick={() => navigate(-1)} />
        </div>
      )}

      <div className={styles.titleViewWrapper}>
        <h6 id="payoutsTitle" className={styles.title}>
          {lang.payoutHistory()}
        </h6>

        {(isMobile || isTablet) && (allTransactions?.length ?? 0) > 0 && (
          <MobileFromToDatePicker
            label={lang.commonDate()}
            initialState={{
              startDate: oldestPayoutDate,
              endDate: new Date(),
            }}
            maxDateCalendar={new Date()}
            minDateCalendar={oldestPayoutDate}
            onDateSelect={handleDateChange}
          />
        )}
      </div>

      {(isMobile || isTablet) && (
        <div className={styles.itemWrapper}>
          {payoutsFromHook.length === 0 && (
            <div className={styles.wrapperNoTransactions}>
              <img className={styles.noMessagesLogo} src={noMessagesLogo} alt="no messages logo" />
              <p className={styles.noMessageText}>{lang.noMatchesDateRange()}</p>
            </div>
          )}

          {payoutsFromHook.map((item) => {
            const payment = item.orderOrPayment as Payment;
            return (
              <ClickableDiv key={payment.payment_reference} onClick={() => handleClick(payment)}>
                <TransactionItem payment={payment} />
              </ClickableDiv>
            );
          })}
        </div>
      )}

      {isMobile && showModal && <Modal handleModal={handleModalTransfer} infoModal={infoModal} />}
    </div>
  );
};

export default PayoutHistoryPage;
